import {Component, Input, OnInit} from '@angular/core';
import {ModalDialogDismissOption} from '@shared/confirmation-dialog/modal-dialog-dismiss-option';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {GatewayRegex} from '@models/adminstration/gateway/gateway-configuration/gateway-regex';
import {GatewaySignals} from '@models/adminstration/gateway/gateway-configuration/gateway-signals';
import {Alias} from '@models/adminstration/gateway/gateway-configuration/alias';
import {UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {GatewayValues} from '@models/adminstration/gateway/gateway-configuration/gateway-values';
import {GatewayRegexService} from '@services/administration/gateway/regex-service/gateway-regex.service';
import {SortingService} from '@services/administration/sorting/sorting.service';

@Component({
    selector: 'scitis-admin-gateway-aliases-edit',
    templateUrl: './gateway-aliases-edit.component.html',
    styleUrls: ['./gateway-aliases-edit.component.scss'],
    standalone: false
})
export class GatewayAliasesEditComponent implements OnInit {

  @Input()
  gatewaySignalsConfig: GatewaySignals = new GatewaySignals();

  copyGatewaySignalsConfig: GatewaySignals = new GatewaySignals();

  gatewayAliasForm: UntypedFormGroup = new UntypedFormGroup({});
  newRegex: GatewayRegex = new GatewayRegex();
  duplicatedAliasFound: boolean = false;
  mask: string = '';
  nodeId: string = '/[^a-zA-Z0-9]/g';
  variableDatatype: string = '/^[0-9].+$/';
  variableDimension: string = '/[a-zA-Z0-9_]{300}';
  duplicates: GatewayValues[] = [];
  submitted: boolean = false;
  isSysRegexCollapsed = true;
  private currentActiveInlineEditField: {index?:number, field:'flags' | 'regexp' | 'replacement' | undefined} = {index: undefined, field: undefined };
  inlineDuplicateRegex: number[] = [];

  constructor(private activeModal: NgbActiveModal,
              private regexService: GatewayRegexService,
              private sortingService: SortingService) {
  }

  ngOnInit() {
    this.copyGatewaySignalsConfig = JSON.parse(JSON.stringify(this.gatewaySignalsConfig));
    this.setAlias();
    this.generateGatewayAliasForm(this.copyGatewaySignalsConfig);
    this.resetAndSortValues(this.copyGatewaySignalsConfig.values);
    this.duplicatedAliasFound = this.verifyAliasDuplicates(this.copyGatewaySignalsConfig.values);
  }

  // convenience getter for easy access to form fields
  get f() {
    return this.gatewayAliasForm.controls;
  }

  get isDisabledSaveBtn(): boolean {
    const isFormValid = this.gatewayAliasForm.invalid || this.duplicatedAliasFound || this.inlineDuplicateRegex.length > 0;
    return this.submitted ? isFormValid : this.submitted && isFormValid;
  };

  onCancel() {
    this.activeModal.dismiss(ModalDialogDismissOption.CANCEL);
  }

  onClose() {
    this.activeModal.dismiss(ModalDialogDismissOption.CLOSE);
  }

  onSave() {
    this.submitted = true;
    // stop here if form is invalid
    if (this.gatewayAliasForm.invalid) {
      return;
    }
    this.updateGatewayAliasProperties();
    this.activeModal.close(this.copyGatewaySignalsConfig);
  }

  addString(string: string) {
    if (!this.mask.includes('{' + string + '}')) {
      this.mask = this.mask + '{' + string + '}';
    }
  }

  onRemoveRegex(regex: any) {
    const index = this.copyGatewaySignalsConfig.alias.regexlist.indexOf(regex);
    if (index >= 0) {
      this.copyGatewaySignalsConfig.alias.regexlist.splice(index, 1);
    }
    this.updateResult();
  }

  onAddRegex() {
    if (!this.copyGatewaySignalsConfig.alias.regexlist) {
      this.copyGatewaySignalsConfig.alias.regexlist = [];
    }

    for (const regex of this.copyGatewaySignalsConfig.alias.regexlist) {
      if (regex.regexp === this.newRegex.regexp) {
        console.log('RegexComponent. Regex already inside the list');
        return;
      }
    }
    if (this.newRegex) {
      this.copyGatewaySignalsConfig.alias.regexlist.push({
        regexp: this.newRegex.regexp,
        replacement: this.newRegex.replacement,
        flags: this.newRegex.flags
      });
      this.newRegex = { flags: '', regexp: '', replacement: '' };
    }
    this.updateResult();
  }

  getAliasValue(value: GatewayValues): string {
    let regexList: GatewayRegex[];
    if (!this.copyGatewaySignalsConfig.alias.regexlist) {
      regexList = [];
    } else {
      regexList = this.copyGatewaySignalsConfig.alias.regexlist;
    }
    let tmpString = this.regexService.fillMaskFromOPCVar(this.mask, value);
    tmpString = this.regexService.updateStringWithRegex(tmpString, regexList);
    return tmpString;
  }

  updateResult() {
    this.duplicatedAliasFound = this.verifyAliasDuplicates(this.copyGatewaySignalsConfig.values);
  }

  getDuplicatedClass(value: GatewayValues): string {
    if (this.duplicates.indexOf(value) > -1) {
      return 'text-danger';
    }
    return '';
  }

  setEditClass(id: number, field: any) {
    this.currentActiveInlineEditField = { index: id, field: field };
  }

  onCancelEditableRegex(id:number) {
    this.currentActiveInlineEditField = { index: undefined, field: undefined };
    if(this.inlineDuplicateRegex.includes(id)) {
      const index = this.inlineDuplicateRegex.indexOf(id);
      if (index > -1)
        this.inlineDuplicateRegex.splice(index, 1);
    }
  }

  onSaveEditableRegex(event: any, id: number, field: string) {
    switch (field) {
      case 'flags': {
        this.copyGatewaySignalsConfig.alias.regexlist[id].flags = event.value;
        this.currentActiveInlineEditField = { index: undefined, field: undefined };
        break;
      }
      case 'regexp': {
        const isDuplicate = this.copyGatewaySignalsConfig.alias.regexlist
          .find((regex, i) => i !== id && regex.regexp === event.value);
        if(isDuplicate) {
          if(!this.inlineDuplicateRegex.includes(id))
            this.inlineDuplicateRegex.push(id);
        } else {
          this.copyGatewaySignalsConfig.alias.regexlist[id].regexp = event.value;
          this.currentActiveInlineEditField = { index: undefined, field: undefined };
          if(this.inlineDuplicateRegex.includes(id)) {
            const index = this.inlineDuplicateRegex.indexOf(id);
            if (index > -1)
              this.inlineDuplicateRegex.splice(index, 1);
          }
        }
        break;
      }
      case 'replacement': {
        this.copyGatewaySignalsConfig.alias.regexlist[id].replacement = event.value;
        this.currentActiveInlineEditField = { index: undefined, field: undefined };
        break;
      }
    }
    this.updateResult();
  }

  regexEditFieldClass(id: number, field: string) {
    if(id === this.currentActiveInlineEditField.index && this.currentActiveInlineEditField.field === field )
      return 'form-inline-edit-active';
    else
      return 'form-inline-edit-inactive';
  }

  isDuplicateRegex(id: number) {
    return this.inlineDuplicateRegex.includes(id);
  }

  private generateGatewayAliasForm(gatewayConfig: GatewaySignals) {
    this.gatewayAliasForm = new UntypedFormGroup({
      mask: new UntypedFormControl(gatewayConfig.alias.mask, [Validators.required]),
      regexp: new UntypedFormControl(null),
      flags: new UntypedFormControl(null),
      replacement: new UntypedFormControl(null),
    });
  }

  private setAlias() {
    if (!this.copyGatewaySignalsConfig.alias) {
      this.copyGatewaySignalsConfig.alias = new Alias('{name}', []);
    }
    this.mask = this.copyGatewaySignalsConfig.alias.mask;
  }

  private verifyAliasDuplicates(values: GatewayValues[]): boolean {
    if (values === null || values.length <= 0) {
      return false;
    }
    this.duplicates = this.generateMapOfDuplicateAliases(values);
    return this.duplicates.length > 0;
  }

  resetAndSortValues(values: GatewayValues[]): GatewayValues[] {
    values.sort((a, b) => this.sortingService.sortByName(a, b));
    return values;
  }

  private generateMapOfDuplicateAliases(values: GatewayValues[]) {
    const tmpValuesArray: GatewayValues[] = [];
    const tmpAliasesArray: string[] = [];
    for (const val of values) {
      val.alias = this.getAliasValue(val);
      if (val.alias !== '') {
        tmpValuesArray.push(val);
        tmpAliasesArray.push(val.alias);
      }
    }
    if (tmpValuesArray.length <= 0) {
      return [];
    }
    const duplicateAlias = tmpAliasesArray.reduce(function (acc: string[], el, i, arr) {
      if (arr.indexOf(el) !== i && acc.indexOf(el) < 0) {
        acc.push(el);
      }
      return acc;
    }, []);

    const duplicates: GatewayValues[] = [];
    for (const val of tmpValuesArray) {
      for (const s of duplicateAlias) {
        if (val.alias === s) {
          duplicates.push(val);
        }
      }
    }
    return duplicates;
  }

  private updateGatewayAliasProperties() {
    this.copyGatewaySignalsConfig.alias.mask = this.f.mask.value;
    for (const val of this.copyGatewaySignalsConfig.values) {
      val.alias = this.getAliasValue(val);
    }
  }
}
